<script setup lang="ts">
import Mousetrap from 'mousetrap';
import useLocale from '../../composables/useLocale';
import { computed, onMounted, onUnmounted } from 'vue';
import { useDisplay } from 'vuetify';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faGaugeHigh } from '@fortawesome/pro-regular-svg-icons';
import { MAX_PLAYBACK_RATE, MIN_PLAYBACK_RATE } from '../../stores/setting';

library.add(faGaugeHigh);

type Emits = {
  (e: 'update:model-value', value: string | number | undefined): void;
};
const emit = defineEmits<Emits>();

type Props = {
  modelValue: string | number | undefined;
  hasMediaAvailable?: boolean;
};
const props = defineProps<Props>();

const { t } = useLocale();
const { smAndDown } = useDisplay();

const step = 0.05;

const numberInputValue = computed({
  get: () => Number(props.modelValue) || 1.0,
  set: (val) => emit('update:model-value', val),
});

onMounted(() => {
  Mousetrap.bind('>', () => {
    numberInputValue.value = Math.round(Math.min(numberInputValue.value + step, MAX_PLAYBACK_RATE) * 100) / 100;
    return false;
  });

  Mousetrap.bind('<', () => {
    numberInputValue.value = Math.round(Math.max(numberInputValue.value - step, MIN_PLAYBACK_RATE) * 100) / 100;
    return false;
  });
});

onUnmounted(() => {
  Mousetrap.unbind('>');
  Mousetrap.unbind('<');
});
</script>

<template>
  <v-list-item
    density="compact"
    :disabled="!hasMediaAvailable"
    data-testid="bp-setting-narration-speed"
    :title="t('label_playback_rate')"
    :aria-label="t('label_playback_rate')"
  >
    <template #prepend>
      <FontAwesomeIcon class="pr-3" :size="!smAndDown ? 'xl' : 'lg'" icon="fa-regular fa-gauge-high"></FontAwesomeIcon>
    </template>
    <template v-if="!hasMediaAvailable" #subtitle>
      <span>{{ t('no_media_playback') }}</span>
    </template>
    <template #append>
      <div class="d-flex align-center ml-4">
        <v-number-input
          v-model="numberInputValue"
          class="my-1 text-on-surface"
          variant="outlined"
          density="compact"
          flat
          :min="MIN_PLAYBACK_RATE"
          :max="MAX_PLAYBACK_RATE"
          :precision="2"
          :step="step"
          :counter-value="1"
          inset
          control-variant="split"
          min-width="180"
          suffix="x"
          aria-live="polite"
          aria-atomic="true"
          :aria-label="t('book_player.label_current_narration_speed')"
          style="height: 40px"
        >
          <template #decrement="{ props }">
            <v-btn
              :disabled="false"
              v-bind="props"
              class="rounded-lg"
              variant="plain"
              icon="mdi-minus"
              color="on-secondary"
              :ripple="false"
              :aria-label="t('book_player.button_decrease_playback_rate')"
            />
          </template>
          <template #increment="{ props }">
            <v-btn
              :disabled="false"
              v-bind="props"
              class="rounded-lg"
              variant="plain"
              icon="mdi-plus"
              color="on-secondary"
              :ripple="false"
              :aria-label="t('book_player.button_increase_playback_rate')"
            />
          </template>
        </v-number-input>
      </div>
    </template>
  </v-list-item>
</template>

<style scoped lang="scss">
:deep(.v-icon) {
  opacity: 1 !important;
}
:deep(.v-text-field__suffix) {
  color: var(--v-theme-on-surface);
}
</style>
